import React, { useState } from 'react';
import Header from '../components/Header';
import { Mail, Phone } from 'lucide-react';
import { supabase } from '../lib/supabase';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    companyUrl: '',
    currentChannel: '',
    tier: '',
    unfairAdvantage: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.companyUrl,
            company: formData.companyUrl,
            message: formData.unfairAdvantage,
            service_interest: formData.tier,
            budget_range: formData.currentChannel,
          }
        ]);

      if (error) throw error;

      setSubmitStatus({
        type: 'success',
        message: 'Application submitted successfully! We\'ll be in touch soon.'
      });
      
      setFormData({
        name: '',
        companyUrl: '',
        currentChannel: '',
        tier: '',
        unfairAdvantage: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit application. Please try again or email us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Contact Section */}
      <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Apply for Access
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
              We operate with a capped client roster to maintain quality. We are currently accepting 2 partners for next month.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <div className="space-y-8">
                <a 
                  href="mailto:contact@attentionlabs.com"
                  className="flex items-start gap-4 p-6 border border-white/10 rounded-lg hover:border-white/30 transition-colors group"
                >
                  <Mail className="text-purple-400 mt-1 group-hover:scale-110 transition-transform" size={24} />
                  <div>
                    <p className="text-sm text-white/50 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>E-mail</p>
                    <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      contact@attentionlabs.com
                    </p>
                  </div>
                </a>

                <a 
                  href="tel:+919871065446"
                  className="flex items-start gap-4 p-6 border border-white/10 rounded-lg hover:border-white/30 transition-colors group"
                >
                  <Phone className="text-purple-400 mt-1 group-hover:scale-110 transition-transform" size={24} />
                  <div>
                    <p className="text-sm text-white/50 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Phone</p>
                    <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      +91 9871065446
                    </p>
                  </div>
                </a>
              </div>
            </div>

            {/* Application Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-purple-400 focus:outline-none transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Company URL
                  </label>
                  <input
                    type="url"
                    name="companyUrl"
                    value={formData.companyUrl}
                    onChange={handleChange}
                    placeholder="https://"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-purple-400 focus:outline-none transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Current Distribution Channel (if any)
                  </label>
                  <input
                    type="text"
                    name="currentChannel"
                    value={formData.currentChannel}
                    onChange={handleChange}
                    placeholder="e.g., YouTube, LinkedIn, Newsletter"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-purple-400 focus:outline-none transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Which Tier interests you?
                  </label>
                  <select
                    name="tier"
                    value={formData.tier}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-purple-400 focus:outline-none transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    required
                  >
                    <option value="">Select a tier</option>
                    <option value="sprint">The Protocol Sprint ($3,000)</option>
                    <option value="founder-os">The Founder OS ($6,000/month)</option>
                    <option value="enterprise">Enterprise Media Build (Custom)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    The Filter Question: What is your unfair advantage?
                  </label>
                  <p className="text-xs text-white/50 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    If you have no product, we cannot help you distribute it.
                  </p>
                  <textarea
                    name="unfairAdvantage"
                    value={formData.unfairAdvantage}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Tell us what makes you different..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-purple-400 focus:outline-none transition-colors resize-none"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Form'}
                </button>

                {submitStatus.message && (
                  <div
                    className={`p-4 rounded-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                        : 'bg-red-500/10 border border-red-500/20 text-red-400'
                    }`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {submitStatus.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 sm:py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://framerusercontent.com/images/mCmSpcpcYCZJTSJSoearkxxG0FQ.png?width=236&height=240" 
                  alt="AttentionLabs" 
                  className="h-10 w-10"
                />
                <span className="text-white font-bold text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  AttentionLabs
                </span>
              </div>
              <p className="text-white/70 text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                811, Tower C, Unitech Business Zone<br />
                Sector 50, Gurugram, India
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Links</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/#clients" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Clients</a></li>
                <li><a href="/#portfolio" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Portfolio</a></li>
                <li><a href="/#why-choose-us" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Why Choose Us</a></li>
                <li><a href="/#testimonials" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Testimonials</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pages</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Home</a></li>
                <li><a href="/portfolio" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Portfolio</a></li>
                <li><a href="/casestudies" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Case Studies</a></li>
                <li><a href="/contact" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Contact</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Socials</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="https://www.instagram.com/attention.labs/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Instagram</a></li>
                <li><a href="https://www.youtube.com/@AttentionLabs-br" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>YouTube</a></li>
                <li><a href="https://www.linkedin.com/company/attentionlabscom/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>LinkedIn</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              Visioned and Crafted by AttentionLabs © All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
